import {
	createRootRoute,
	HeadContent,
	Scripts,
	useRouterState,
} from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { Footer } from "#/components/footer";
import { Navbar } from "#/components/navbar";
import { RootErrorComponent } from "#/components/RootErrorComponent";
import { SignInModalProvider } from "#/hooks/useSignInModal";
import i18n, { detectAndApplyLanguage } from "#/lib/i18n";
import { cn } from "#/lib/utils";
import { api } from "../../convex/_generated/api";
import ConvexProvider from "../integrations/convex/provider";

import appCss from "../styles.css?url";

export const Route = createRootRoute({
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1, viewport-fit=cover",
			},
			{ title: "Re:Disc — Buy & Sell Disc Golf Discs" },
			{
				name: "description",
				content:
					"Re:Disc is a free disc golf marketplace. Browse and sell used discs — no platform fees, ever. Connect directly with players across Europe.",
			},
			{ property: "og:site_name", content: "Re:Disc" },
			{ property: "og:type", content: "website" },
			{ property: "og:title", content: "Re:Disc — Disc Golf Marketplace" },
			{
				property: "og:description",
				content:
					"Browse and sell used disc golf discs. No platform fees — connect directly with players.",
			},
			// TODO: replace with absolute URL once production domain is set
			{ property: "og:image", content: "/og-image.png" },
			{ property: "og:image:width", content: "1200" },
			{ property: "og:image:height", content: "630" },
			{ name: "twitter:card", content: "summary_large_image" },
			{ name: "twitter:title", content: "Re:Disc — Disc Golf Marketplace" },
			{
				name: "twitter:description",
				content:
					"Browse and sell used disc golf discs. No platform fees — connect directly with players.",
			},
			{ name: "twitter:image", content: "/og-image.png" },
			{ name: "apple-mobile-web-app-capable", content: "yes" },
			{ name: "apple-mobile-web-app-title", content: "Re:Disc" },
			{
				name: "apple-mobile-web-app-status-bar-style",
				content: "black-translucent",
			},
			{ name: "mobile-web-app-capable", content: "yes" },
		],
		links: [
			{ rel: "stylesheet", href: appCss },
			{ rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32x32.png",
			},
			{ rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#e05500" },
			{ rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
			{ rel: "manifest", href: "/manifest.json" },
		],
	}),
	errorComponent: ({ error, reset }) => (
		<RootErrorComponent error={error as Error} reset={reset} />
	),
	shellComponent: RootDocument,
});

const themeScript = `(function(){try{var t=localStorage.getItem('theme');if(t==='dark'||(t!=='light'&&window.matchMedia('(prefers-color-scheme: dark)').matches)){document.documentElement.classList.add('dark')}}catch(e){}})()`;

function NavigationProgress() {
	const isNavigating = useRouterState({ select: (s) => s.isLoading });
	return (
		<div
			aria-hidden
			className={cn(
				"fixed top-0 inset-x-0 h-[2px] z-[9999] overflow-hidden pointer-events-none transition-opacity duration-500",
				isNavigating ? "opacity-100" : "opacity-0",
			)}
		>
			<div
				className={cn(
					"absolute inset-y-0 bg-primary",
					isNavigating && "nav-progress-sweep",
				)}
			/>
		</div>
	);
}

function SyncUser() {
	const { isAuthenticated } = useConvexAuth();
	const syncUser = useMutation(api.users.syncUser);
	useEffect(() => {
		if (isAuthenticated) syncUser();
	}, [isAuthenticated, syncUser]);
	return null;
}

function useDocumentTheme() {
	const [theme, setTheme] = useState<"light" | "dark">("light");
	useEffect(() => {
		const root = document.documentElement;
		setTheme(root.classList.contains("dark") ? "dark" : "light");
		const observer = new MutationObserver(() => {
			setTheme(root.classList.contains("dark") ? "dark" : "light");
		});
		observer.observe(root, { attributes: true, attributeFilter: ["class"] });
		return () => observer.disconnect();
	}, []);
	return theme;
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const theme = useDocumentTheme();
	useEffect(() => {
		detectAndApplyLanguage();
	}, []);
	useEffect(() => {
		if (import.meta.env.PROD && "serviceWorker" in navigator) {
			fetch("/sw.js", { method: "HEAD" })
				.then((r) => {
					if (r.ok) navigator.serviceWorker.register("/sw.js");
				})
				.catch(() => {});
		}
	}, []);
	useEffect(() => {
		const repaint = () => {
			if (document.visibilityState !== "visible") return;
			const el = document.documentElement;
			requestAnimationFrame(() => {
				el.style.opacity = "0.9999";
				requestAnimationFrame(() => {
					el.style.opacity = "";
				});
			});
		};
		document.addEventListener("visibilitychange", repaint);
		return () => document.removeEventListener("visibilitychange", repaint);
	}, []);
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: blocking theme script must run before paint */}
				<script dangerouslySetInnerHTML={{ __html: themeScript }} />
				<HeadContent />
			</head>
			<body>
				<I18nextProvider i18n={i18n}>
					<ConvexProvider>
						<SignInModalProvider>
							<SyncUser />
							<NavigationProgress />
							<div className="flex min-h-screen flex-col">
								<Navbar />
								<main className="flex flex-1 flex-col">{children}</main>
								<Footer />
							</div>
							<Toaster richColors position="bottom-right" theme={theme} />
						</SignInModalProvider>
					</ConvexProvider>
				</I18nextProvider>
				<Scripts />
			</body>
		</html>
	);
}
